import { KeyIcon } from "../foundation/KeyIcon.js";
// Extracted from web/App.tsx; behavior and declaration bodies preserved.
import React, { useEffect, useState } from "react";

import { ArrowRight, Eye, EyeOff, Table2 } from "lucide-react";

import type { Session } from "../../shared/contracts";
import { PRODUCT_RELEASE } from "../../shared/version";
import { api } from "../api";
import { Button, Field, Notice, errorText } from "../components/Primitives";

import { Brand } from "../foundation/Brand.js";
export function Authentication({
  setup,
  requiresToken,
  onSession,
  initialError,
}: {
  setup: boolean;
  requiresToken: boolean;
  onSession: (s: Session) => void;
  initialError: string;
}) {
  const [form, setForm] = useState({
    organizationCode: "",
    organizationName: "",
    name: "",
    email: "",
    password: "",
    setupToken: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [passwordVisible, setPasswordVisible] = useState(false);
  useEffect(() => setPasswordVisible(false), [setup]);
  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [k]: e.target.value }),
  });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r: any = await api(setup ? "/setup/bootstrap" : "/auth/login", {
        method: "POST",
        body: JSON.stringify(form),
      });
      onSession(r.session || r);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-page">
      <aside className="auth-story">
        <Brand large />
        <div className="auth-story-copy">
          <span className="eyebrow">
            FROM BUSINESS MEANING TO DATABASE DESIGN
          </span>
          <h1>
            Make complex
            <br />
            data feel
            <br />
            <em>connected.</em>
          </h1>
          <p>
            A thoughtful workspace for modeling systems, preserving meaning, and
            building with confidence.
          </p>
        </div>
        <div className="auth-model-art" aria-hidden="true">
          <div className="art-table">
            <span>
              <KeyIcon />
              Customer
            </span>
            <i>
              customer_id <b>uuid</b>
            </i>
            <i>
              display_name <b>text</b>
            </i>
            <i>
              created_at <b>date</b>
            </i>
          </div>
          <div className="art-connector" />
          <div className="art-table small">
            <span>
              <Table2 size={13} />
              Order
            </span>
            <i>
              order_id <b>uuid</b>
            </i>
            <i>
              customer_id <b>uuid</b>
            </i>
          </div>
        </div>
        <div className="auth-caption">
          <span className="status-dot" />
          Your models. Your environment. Your control.
        </div>
      </aside>
      <main className="auth-form-area">
        <div className="auth-form-card">
          <div className="auth-badge">{PRODUCT_RELEASE.toUpperCase()} · DEVELOPMENT WORKSPACE</div>
          <h2>{setup ? "Create your studio" : "Welcome to your studio"}</h2>
          <p>
            {setup
              ? "Set up the first organization and its administrator."
              : "Sign in to continue your modeling work."}
          </p>
          {error && <Notice kind="error">{error}</Notice>}
          <form onSubmit={submit}>
            {setup && (
              <div className="form-row">
                <Field label="Your name">
                  <input required autoComplete="name" {...field("name")} />
                </Field>
                <Field label="Organization name">
                  <input
                    required
                    autoComplete="organization"
                    {...field("organizationName")}
                  />
                </Field>
              </div>
            )}
            <Field
              label="Organization code"
              hint="The code identifies your studio. Your account still controls access."
            >
              <input
                required
                autoComplete="organization"
                placeholder="Your organization code"
                {...field("organizationCode")}
              />
            </Field>
            <Field label="Email address">
              <input
                type="email"
                required
                autoComplete="username"
                placeholder="you@organization.com"
                {...field("email")}
              />
            </Field>
            <Field
              label="Password"
              hint={
                setup
                  ? "Choose at least 12 characters. No default password is provided."
                  : undefined
              }
            >
              <div className="password-input">
                <input
                  type={passwordVisible ? "text" : "password"}
                  aria-label="Password"
                  minLength={setup ? 12 : undefined}
                  required
                  autoComplete={setup ? "new-password" : "current-password"}
                  {...field("password")}
                />
                <button type="button" aria-label={passwordVisible ? "Hide password" : "Show password"} aria-pressed={passwordVisible} title={passwordVisible ? "Hide password" : "Show password"} onClick={() => setPasswordVisible(!passwordVisible)}>{passwordVisible ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
              </div>
            </Field>
            {setup && requiresToken && (
              <Field
                label="Deployment setup token"
                hint="Use the token supplied by the installation operator."
              >
                <input
                  type="password"
                  required
                  autoComplete="off"
                  {...field("setupToken")}
                />
              </Field>
            )}
            <Button type="submit" variant="primary full-width" busy={busy}>
              {setup ? "Create studio" : "Open workspace"}
              <ArrowRight size={16} />
            </Button>
          </form>
          <p className="auth-fine-print">
            {setup
              ? "First-use setup is available only while the installation has no organization."
              : "Need access? Contact your studio administrator."}
          </p>
        </div>
        <div className="auth-footer">
          Designed for the details.<span>DataGlow ER Designer</span>
        </div>
      </main>
    </div>
  );
}
